import {
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  BankStatusChip,
  getBankStatusLabel,
  CustomNoRowsOverlay,
  ProductCompanyStatusChip,
  getProductCompanyStatusLabel,
} from 'components';
import {
  GridColDef,
  useGridApiRef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-premium';
import type { Book } from 'library/types';
import apiService from 'library/api-service';
import { useDataGridLocaleText } from 'hooks';
import type { GeneralReportRow } from './types';
import { buildGeneralReportRows } from './utils';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage, useIntl } from 'react-intl';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import usedBanksActions from 'store/features/used-banks/actions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildGeneralReportFilterOptions, createGeneralReportLookupComparator } from './filter-options';
import { EXPORT_TYPE_ENUM, BOOK_BANK_STATUS_DISPLAY_ORDER, PRODUCT_COMPANY_STATUS_DISPLAY_ORDER } from 'enums';
import { selectProjects, selectProjectsIsEmpty, selectProjectsLoading } from 'store/features/projects/selector';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { selectUsedBanks, selectUsedBanksIsEmpty, selectUsedBanksLoading } from 'store/features/used-banks/selector';

const FILTER_PANEL_PROPS = {
  filterFormProps: {
    logicOperatorInputProps: { variant: 'outlined' as const, size: 'small' as const },
    columnInputProps: { variant: 'outlined' as const, size: 'small' as const, sx: { mt: 'auto' } },
    operatorInputProps: { variant: 'outlined' as const, size: 'small' as const, sx: { mt: 'auto' } },
    valueInputProps: {
      InputComponentProps: { variant: 'outlined' as const, size: 'small' as const },
    },
  },
};

const SINGLE_SELECT_FILTER_OPERATORS = getGridSingleSelectOperators();

const NULLABLE_SINGLE_SELECT_FILTER_OPERATORS = [
  ...SINGLE_SELECT_FILTER_OPERATORS,
  ...getGridStringOperators().filter((operator) => operator.value === 'isEmpty' || operator.value === 'isNotEmpty'),
];

const GeneralReport = () => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();
  const dataGridLocaleText = useDataGridLocaleText();

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const productsLoading = useAppSelector(selectProjectsLoading);
  const usedBanks = useAppSelector(selectUsedBanks);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);
  const usedBanksLoading = useAppSelector(selectUsedBanksLoading);

  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const rows = useMemo(() => buildGeneralReportRows(books, products, usedBanks), [books, products, usedBanks]);

  const missingValue = useMemo(
    () =>
      intl.formatMessage({
        id: 'generalReport.missingValue',
        defaultMessage: 'Bilgi yok',
      }),
    [intl]
  );

  const bookFilterOptions = useMemo(
    () =>
      buildGeneralReportFilterOptions(
        books.map((book) => ({ value: book.id, label: book.companyName ?? '' })),
        rows.map((row) => ({ value: row.bookId, label: row.book })),
        intl.locale
      ),
    [books, intl.locale, rows]
  );
  const productFilterOptions = useMemo(
    () =>
      buildGeneralReportFilterOptions(
        products.map((product) => ({ value: product.id, label: product.title })),
        rows.map((row) => ({ value: row.projectId, label: row.project })),
        intl.locale
      ),
    [intl.locale, products, rows]
  );
  const bankFilterOptions = useMemo(
    () =>
      buildGeneralReportFilterOptions(
        usedBanks.map((bank) => ({ value: bank.id, label: bank.title })),
        rows.flatMap((row) => (row.bankId == null ? [] : [{ value: row.bankId, label: row.bankName ?? '' }])),
        intl.locale
      ),
    [intl.locale, rows, usedBanks]
  );
  const bookFilterOptionById = useMemo(
    () => new Map(bookFilterOptions.map((option) => [option.value, option])),
    [bookFilterOptions]
  );
  const productFilterOptionById = useMemo(
    () => new Map(productFilterOptions.map((option) => [option.value, option])),
    [productFilterOptions]
  );
  const bankFilterOptionById = useMemo(
    () => new Map(bankFilterOptions.map((option) => [option.value, option])),
    [bankFilterOptions]
  );
  const bookSortComparator = useMemo(
    () => createGeneralReportLookupComparator(bookFilterOptions, intl.locale, missingValue),
    [bookFilterOptions, intl.locale, missingValue]
  );
  const productSortComparator = useMemo(
    () => createGeneralReportLookupComparator(productFilterOptions, intl.locale, missingValue),
    [intl.locale, missingValue, productFilterOptions]
  );
  const bankSortComparator = useMemo(
    () => createGeneralReportLookupComparator(bankFilterOptions, intl.locale, missingValue),
    [bankFilterOptions, intl.locale, missingValue]
  );

  const reportCounts = useMemo(
    () => ({
      books: new Set(rows.map((row) => row.bookId)).size,
      products: new Set(rows.map((row) => row.projectId)).size,
      rows: rows.length,
    }),
    [rows]
  );

  const renderMissingValue = useCallback(
    () => (
      <Typography component="span" variant="body2" color="text.disabled" aria-label={missingValue}>
        —
      </Typography>
    ),
    [missingValue]
  );

  const columns: GridColDef<GeneralReportRow>[] = useMemo(
    () => [
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'generalReport.table.book', defaultMessage: 'Proje' }),
        minWidth: 220,
        flex: 1.15,
        type: 'singleSelect',
        filterOperators: SINGLE_SELECT_FILTER_OPERATORS,
        valueOptions: bookFilterOptions,
        valueFormatter: (value: number | null) =>
          value == null ? missingValue : (bookFilterOptionById.get(value)?.label ?? `#${value}`),
        renderCell: (params: GridRenderCellParams<GeneralReportRow, number | null>) =>
          params.value == null
            ? renderMissingValue()
            : (bookFilterOptionById.get(params.value)?.displayLabel ?? `#${params.value}`),
        sortComparator: bookSortComparator,
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'generalReport.table.project', defaultMessage: 'Ürün' }),
        minWidth: 180,
        flex: 1,
        type: 'singleSelect',
        filterOperators: SINGLE_SELECT_FILTER_OPERATORS,
        valueOptions: productFilterOptions,
        valueFormatter: (value: number | null) =>
          value == null ? missingValue : (productFilterOptionById.get(value)?.label ?? `#${value}`),
        renderCell: (params: GridRenderCellParams<GeneralReportRow, number | null>) =>
          params.value == null
            ? renderMissingValue()
            : (productFilterOptionById.get(params.value)?.displayLabel ?? `#${params.value}`),
        sortComparator: productSortComparator,
      },
      {
        field: 'companyCode',
        headerName: intl.formatMessage({ id: 'generalReport.table.companyCode', defaultMessage: 'Şirket Kodu' }),
        minWidth: 135,
        flex: 0.75,
        valueFormatter: (value) => value || missingValue,
        renderCell: (params: GridRenderCellParams<GeneralReportRow, string | null>) =>
          params.value ? params.value : renderMissingValue(),
      },
      {
        field: 'companyTitle',
        headerName: intl.formatMessage({ id: 'generalReport.table.companyTitle', defaultMessage: 'Şirket Ünvanı' }),
        minWidth: 220,
        flex: 1.15,
        valueFormatter: (value) => value || missingValue,
        renderCell: (params: GridRenderCellParams<GeneralReportRow, string | null>) =>
          params.value ? params.value : renderMissingValue(),
      },
      {
        field: 'companyStatusByProduct',
        headerName: intl.formatMessage({
          id: 'generalReport.table.companyStatusByProduct',
          defaultMessage: 'Ürün Şirket Durumu',
        }),
        minWidth: 190,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        type: 'singleSelect',
        filterOperators: NULLABLE_SINGLE_SELECT_FILTER_OPERATORS,
        valueOptions: PRODUCT_COMPANY_STATUS_DISPLAY_ORDER.map((status) => ({
          value: status,
          label: getProductCompanyStatusLabel(status, intl),
        })),
        valueFormatter: (value) => (value == null ? missingValue : getProductCompanyStatusLabel(value, intl)),
        renderCell: (params) =>
          params.value == null ? renderMissingValue() : <ProductCompanyStatusChip status={params.value} />,
      },
      {
        field: 'bankId',
        headerName: intl.formatMessage({ id: 'generalReport.table.bankName', defaultMessage: 'Banka' }),
        minWidth: 180,
        flex: 0.95,
        type: 'singleSelect',
        filterOperators: NULLABLE_SINGLE_SELECT_FILTER_OPERATORS,
        valueOptions: bankFilterOptions,
        valueFormatter: (value: number | null) =>
          value == null ? missingValue : (bankFilterOptionById.get(value)?.label ?? `#${value}`),
        renderCell: (params: GridRenderCellParams<GeneralReportRow, number | null>) =>
          params.value == null
            ? renderMissingValue()
            : (bankFilterOptionById.get(params.value)?.displayLabel ?? `#${params.value}`),
        sortComparator: bankSortComparator,
      },
      {
        field: 'bankStatusByProduct',
        headerName: intl.formatMessage({
          id: 'generalReport.table.bankStatusByProduct',
          defaultMessage: 'Ürün Banka Durumu',
        }),
        minWidth: 180,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        type: 'singleSelect',
        filterOperators: NULLABLE_SINGLE_SELECT_FILTER_OPERATORS,
        valueOptions: BOOK_BANK_STATUS_DISPLAY_ORDER.map((status) => ({
          value: status,
          label: getBankStatusLabel(status, intl),
        })),
        valueFormatter: (value) => (value == null ? missingValue : getBankStatusLabel(value, intl)),
        renderCell: (params) =>
          params.value == null ? renderMissingValue() : <BankStatusChip status={params.value} />,
      },
    ],
    [
      bankFilterOptions,
      bankFilterOptionById,
      bankSortComparator,
      bookFilterOptions,
      bookFilterOptionById,
      bookSortComparator,
      intl,
      missingValue,
      productFilterOptions,
      productFilterOptionById,
      productSortComparator,
      renderMissingValue,
    ]
  );

  const loadReportData = useCallback(
    async (refreshLookups = false) => {
      try {
        setLoading(true);
        setLoadError(false);

        const lookupRefresh = refreshLookups
          ? Promise.all([
              dispatch(projectsActions.getProjects()).unwrap(),
              dispatch(usedBanksActions.getUsedBanks()).unwrap(),
            ])
          : Promise.resolve();
        const [response] = await Promise.all([apiService.getBooks(), lookupRefresh]);

        if (response.isSuccess) {
          setBooks(response.data);
        } else {
          setLoadError(true);
        }
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const onClickDownloadHandler = useCallback(
    async (exportType: EXPORT_TYPE_ENUM) => {
      const fileName = intl.formatMessage({
        id: 'generalReport.exportFileName',
        defaultMessage: 'genel-rapor',
      });

      if (exportType === EXPORT_TYPE_ENUM.EXCEL) {
        await apiRef.current.exportDataAsExcel({ fileName });
        return;
      }

      apiRef.current.exportDataAsCsv({ fileName, utf8WithBom: true });
    },
    [apiRef, intl]
  );

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    void loadReportData();
  }, [loadReportData]);

  const gridLoading = loading || productsLoading || usedBanksLoading;

  return (
    <Stack spacing={2} minWidth={0}>
      <Box
        component="header"
        display="flex"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box
            aria-hidden="true"
            display="grid"
            sx={{
              placeItems: 'center',
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 1.5,
              color: 'primary.main',
              bgcolor: 'action.selected',
            }}>
            <TableRowsIcon />
          </Box>
          <Box minWidth={0}>
            <Typography component="h1" variant="h5" fontWeight={700}>
              <FormattedMessage id="generalReport.title" defaultMessage="Genel Rapor" />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                id="generalReport.description"
                defaultMessage="Proje, ürün, şirket ve banka ilişkilerini satır bazında inceleyin."
              />
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip
            size="small"
            variant="outlined"
            label={intl.formatMessage(
              { id: 'generalReport.stats.books', defaultMessage: '{count} proje' },
              { count: reportCounts.books }
            )}
          />
          <Chip
            size="small"
            variant="outlined"
            label={intl.formatMessage(
              { id: 'generalReport.stats.products', defaultMessage: '{count} ürün' },
              { count: reportCounts.products }
            )}
          />
          <Chip
            size="small"
            color="primary"
            label={intl.formatMessage(
              { id: 'generalReport.stats.rows', defaultMessage: '{count} satır' },
              { count: reportCounts.rows }
            )}
          />
          <Tooltip title={intl.formatMessage({ id: 'generalReport.refresh', defaultMessage: 'Raporu yenile' })}>
            <span>
              <IconButton
                size="small"
                disabled={gridLoading}
                aria-label={intl.formatMessage({ id: 'generalReport.refresh', defaultMessage: 'Raporu yenile' })}
                onClick={() => void loadReportData(true)}>
                {gridLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" disabled={gridLoading} onClick={() => void loadReportData(true)}>
              <FormattedMessage id="generalReport.retry" defaultMessage="Tekrar Dene" />
            </Button>
          }>
          <FormattedMessage
            id="generalReport.loadError"
            defaultMessage="Rapor verileri yüklenemedi. Lütfen tekrar deneyin."
          />
        </Alert>
      )}

      {isEmptyProducts ? (
        <Alert severity="info" icon={false}>
          <FormattedMessage
            id="generalReport.completeAdaptationsText"
            defaultMessage="Sayfayı görüntülemek için lütfen ürün uyarlamasını tamamlayın."
          />
        </Alert>
      ) : (
        <DataGridBox sx={{ width: '100% !important', minWidth: 0, height: 'calc(100vh - 235px)', minHeight: 480 }}>
          <ZebraDataGrid<GeneralReportRow>
            apiRef={apiRef}
            aria-label={intl.formatMessage({ id: 'generalReport.gridLabel', defaultMessage: 'Genel rapor tablosu' })}
            rows={rows}
            columns={columns}
            loading={gridLoading}
            pagination
            pageSizeOptions={[25, 50, 100]}
            checkboxSelection
            disableRowSelectionOnClick
            disableAggregation
            disableRowGrouping
            rowHeight={42}
            columnHeaderHeight={48}
            initialState={{
              density: 'compact',
              pagination: { paginationModel: { page: 0, pageSize: 50 } },
              sorting: {
                sortModel: [
                  { field: 'bookId', sort: 'asc' },
                  { field: 'projectId', sort: 'asc' },
                  { field: 'companyCode', sort: 'asc' },
                  { field: 'bankId', sort: 'asc' },
                ],
              },
            }}
            localeText={dataGridLocaleText}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
              toolbar: CustomToolbar,
            }}
            slotProps={{
              filterPanel: FILTER_PANEL_PROPS,
              toolbar: {
                multiExport: true,
                showMultiSort: true,
                singleFilter: true,
                singleFilterComponent: (
                  <GridToolbarQuickFilter debounceMs={300} sx={{ display: { xs: 'none', sm: 'flex' }, width: 220 }} />
                ),
                exportAsExcelHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.EXCEL),
                exportAsCsvHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.CSV),
              },
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </DataGridBox>
      )}
    </Stack>
  );
};

export default GeneralReport;
